import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Drawer, Stack, useMediaQuery, Typography, ButtonBase, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import MenuCard from './MenuCard';
import { drawerWidth, headerOffset } from 'store/constant';
import { margin } from '@mui/system';
import { IconChevronLeft } from '@tabler/icons-react';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {const { t } = useTranslation();
  
  const theme = useTheme();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const pageImages = useSelector((state) => state.siteInfo.page_images || []);

  
  const drawer = (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
          <LogoSection />
        </Box>
      </Box>
      <BrowserView>
        <PerfectScrollbar
          component="div"
          style={{
            height: !matchUpMd ? 'calc(100vh - 56px)' : `calc(100vh - ${headerOffset}px)`,
            // paddingLeft: '16px',
            // paddingRight: '16px'
          }}
        >
          <MenuList />
          {/* <MenuCard /> */}
          <Stack direction="column" style={{float: 'left', marginLeft: '40px'}}>
            <a variant="h5" style={{ color: '#364152', fontSize: '14px', margin: '10px 0' }} align="center"               href='https://udoc.ishuanyun.com.cn/docs/interface' target="_blank">
              {t('sidebar.docs')}
            </a>
            <a variant="h5" style={{ color: '#364152', fontSize: '14px', margin: '10px 0' }} align="center"               href='https://udoc.ishuanyun.com.cn/docs/problem' target="_blank">
              {t('sidebar.faq')}
            </a>
            <a variant="h5" style={{ color: '#364152', fontSize: '14px', margin: '10px 0' }} align="center"               href='https://udoc.ishuanyun.com.cn/docs/price' target="_blank">
              {t('sidebar.pricing')}
            </a>
            {/* <Typography variant="h5" sx={{ color: '#364152', fontSize: '14px', margin: '10px 0' }} align="center">
              {t('sidebar.integrations')}
            </Typography> */}
            <Typography
              component={RouterLink}
              to="/contact"
              variant="h5"
              sx={{
                color: '#364152',
                fontSize: '14px',
                margin: '10px 0',
                textDecoration: 'none',
                '&:hover': {
                  color: '#1976d2'
                }
              }}
              align="center"
            >
              {t('sidebar.contact')}
            </Typography>
            <Typography
              component={RouterLink}
              to="/chat"
              variant="h5"
              target='_blank'
              sx={{
                color: '#364152',
                fontSize: '14px',
                margin: '10px 0',
                textDecoration: 'none',
                '&:hover': {
                  color: '#1976d2'
                }
              }}
              align="center"
            >
              {t('sidebar.webChat')}
            </Typography>
          </Stack>
        </PerfectScrollbar>
      </BrowserView>
      <MobileView>
        <Box sx={{ px: 2 }}>
          <MenuList />
          <Stack direction="column" sx={{ px: 2, mb: 2 }}>
            <Typography
              component={RouterLink}
              to="/chat"
              sx={{
                color: '#364152',
                fontSize: '14px',
                margin: '10px 0',
                textDecoration: 'none',
                '&:hover': {
                  color: '#1976d2'
                }
              }}
            >
              {t('sidebar.webChat')}
            </Typography>
          </Stack>
          <MenuCard />
          <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
            <Chip
              label={process.env.REACT_APP_VERSION || t('sidebar.unknownVersion')}
              disabled
              chipcolor="secondary"
              size="small"
              sx={{ cursor: 'pointer' }}
            />
          </Stack>
        </Box>
      </MobileView>
    </>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="mailbox folders">
      <Drawer
        container={container}
        variant={matchUpMd ? 'persistent' : 'temporary'}
        anchor="left"
        open={drawerOpen}
        onClose={drawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            color: theme.palette.text.primary,
            borderRight: 'none',
            [theme.breakpoints.up('md')]: {
              top: `${headerOffset}px`
            }
          }
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        {drawer}
      </Drawer>
      {/* Collapse/expand button outside Drawer — always visible */}
      <Tooltip title={drawerOpen ? t('sidebar.collapse') : t('sidebar.expand')} placement="right">
        <ButtonBase
          onClick={drawerToggle}
          sx={{
            position: 'fixed',
            left: drawerOpen ? 245 : 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1300,
            width: 16,
            height: 48,
            backgroundColor: 'background.default',
            borderRadius: drawerOpen ? '10px 0 0 10px' : '0 10px 10px 0',
            border: '1px solid',
            borderColor: 'divider',
            borderRight: drawerOpen ? 'none' : '',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'left 0.3s ease-in-out, background-color 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            <IconChevronLeft size={14} stroke={2} />
          </Box>
        </ButtonBase>
      </Tooltip>
    </Box>
  );
};

Sidebar.propTypes = {
  drawerOpen: PropTypes.bool,
  drawerToggle: PropTypes.func,
  window: PropTypes.object
};

export default Sidebar;
